import React, {useState} from 'react';
import PageToolBar from '../../../components/PageToolBar';
import Block from './block';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import {InsuranceUnit, PayrollCompanySetting} from '../../../api/types';
import {
  ErrorMessage,
  FormGroup,
  FormItemButton,
  FormItemGroup,
  Input,
  RequireMarker,
} from '../../../../../components/form-items';
import {IconButton} from '../../../../../components/Button';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlusCircle, faSpinner} from '@fortawesome/pro-light-svg-icons';
import {usePayrollOptions} from '../../../api/payroll';
import Select from '../../../../../components/Select/Select';
import {v4 as uuid} from 'uuid';
import {DeleteDialog} from '../../../components/Dialog';

const InsuranceApplicantUnit = function () {
  const {data: options, isLoading} = usePayrollOptions('NHIBusinessGroup');
  const {
    control,
    register,
    errors,
    getValues,
    formState: {isSubmitted},
    trigger,
  } = useFormContext<PayrollCompanySetting>();
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const {fields, append, remove} = useFieldArray<InsuranceUnit>({
    control,
    name: 'insuranceUnits',
  });

  React.useEffect(() => {
    if (fields.length === 0) {
      append({insuranceApplicantUnitId: uuid()});
    }
  }, [fields]);

  return (
    <div>
      <PageToolBar pageTitle={'投保單位'} />
      <Block>
        {isLoading && (
          <div className={'flex items-center justify-center gap-[15px] mb-5'}>
            資料載入中
            <FontAwesomeIcon icon={faSpinner} pulse size={'1x'} />
          </div>
        )}
        {!isLoading &&
          fields.map((field, idx) => (
            <FormGroup
              data-test-id={`insurance-applicant-unit-${idx}`}
              columnTemplates={'1fr 175px 130px 100px auto 175px 175px 64px'}
              key={field.id}
              className={
                'pb-5 mb-5 border-b border-b-[#e9eced] gap-x-[15px] last:border-b-0 last:mb-0'
              }
            >
              <input
                className={'min-w-0'}
                type={'hidden'}
                ref={register()}
                name={`insuranceUnits.${idx}.insuranceApplicantUnitId`}
                defaultValue={field.insuranceApplicantUnitId}
                data-test-id={'insurance-applicant-unit-id'}
              />
              <FormItemGroup className={'min-w-0'}>
                <label
                  htmlFor={`insuranceUnits.${idx}.insuranceApplicantUnitName`}
                >
                  投保單位名稱 <RequireMarker />
                </label>
                <Input
                  data-test-id={'unit-name'}
                  name={`insuranceUnits.${idx}.insuranceApplicantUnitName`}
                  id={`insuranceUnits.${idx}.insuranceApplicantUnitName`}
                  ref={register({
                    required: '限輸入一個以上的中文字',
                    validate: {
                      isHans: v => {
                        return (/^[\u4E00-\u9FFF]+$/i.test(v) && v.length >= 2) || '限輸入一個以上的中文字';
                      },
                    },
                  })}
                  maxLength={25}
                  error={
                    errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.insuranceApplicantUnitName
                      ?.message
                  }
                  defaultValue={field.insuranceApplicantUnitName}
                  placeholder={'XXXX股份有限公司'}
                />
                <ErrorMessage>
                  {errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.insuranceApplicantUnitName
                      ?.message}
                </ErrorMessage>
              </FormItemGroup>
              <FormItemGroup className={'min-w-0'}>
                <label htmlFor={`insuranceUnits.${idx}.insuranceNo`}>
                  勞工保險證號(數字) <RequireMarker />
                </label>
                <Input
                  data-test-id={'insurance-no'}
                  name={`insuranceUnits.${idx}.insuranceNo`}
                  id={`insuranceUnits.${idx}.insuranceNo`}
                  ref={register({
                    required: '必填欄位',
                    pattern: {
                      value: /^\d{8}$/,
                      message: '需為8碼數字',
                    },
                    validate: v => {
                      const {insuranceUnits} = getValues();
                      return insuranceUnits.some(
                        (insuranceUnit, compareIdx: number) =>
                          insuranceUnit.insuranceNo === v && compareIdx !== idx,
                      )
                        ? '勞工保險證號不可重覆'
                        : true;
                    },
                  })}
                  maxLength={8}
                  error={
                    errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.insuranceNo?.message
                  }
                  defaultValue={field.insuranceNo}
                  placeholder={'12345678'}
                  onBlur={e => {
                    if (isSubmitted) {
                      trigger(
                        fields
                          .map(
                            (f, fIdx) => `insuranceUnits.${fIdx}.insuranceNo`,
                          )
                          .filter((f, fIdx) => fIdx !== idx),
                      );
                    }
                  }}
                />
                <ErrorMessage>
                  {errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.insuranceNo?.message}
                </ErrorMessage>
              </FormItemGroup>
              <FormItemGroup className={'min-w-0'}>
                <label htmlFor={`insuranceUnits.${idx}.insuranceCheckNo`}>
                  勞工保險證檢查碼 <RequireMarker />
                </label>
                <Input
                  data-test-id={'insurance-check-no'}
                  name={`insuranceUnits.${idx}.insuranceCheckNo`}
                  id={`insuranceUnits.${idx}.insuranceCheckNo`}
                  ref={register({
                    required: '必填欄位',
                    pattern: {
                      value: /^[A-Z]$/,
                      message: '需為大寫英文',
                    },
                  })}
                  maxLength={1}
                  error={
                    errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.insuranceCheckNo?.message
                  }
                  defaultValue={field.insuranceCheckNo}
                  placeholder={'A'}
                />
                <ErrorMessage>
                  {errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.insuranceCheckNo?.message}
                </ErrorMessage>
              </FormItemGroup>
              <FormItemGroup className={'min-w-0'}>
                <label htmlFor={`insuranceUnits.${idx}.occupationalInjuryRate`}>
                  職災費率
                  <RequireMarker />
                </label>
                <Input
                  data-test-id={'occupational-injury-rate'}
                  type={'number'}
                  name={`insuranceUnits.${idx}.occupationalInjuryRate`}
                  id={`insuranceUnits.${idx}.occupationalInjuryRate`}
                  ref={register({
                    required: '必填欄位',
                    min: {
                      value: 0,
                      message: '最小值為0',
                    },
                    max: {
                      value: 100,
                      message: '最大值為100',
                    },
                  })}
                  error={
                    errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.occupationalInjuryRate?.message
                  }
                  defaultValue={field.occupationalInjuryRate}
                  placeholder={'0.17'}
                />
                <ErrorMessage>
                  {errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.occupationalInjuryRate?.message}
                </ErrorMessage>
              </FormItemGroup>
              <div className={'min-w-0 -ml-2'}>
                <FormItemButton>%</FormItemButton>
              </div>
              <FormItemGroup className={'min-w-0'}>
                <label htmlFor={`insuranceUnits.${idx}.nHIUnitCode`}>
                  健保投保單位代碼(數字)
                  <RequireMarker />
                </label>
                <Input
                  data-test-id={'nhi-unit-code'}
                  name={`insuranceUnits.${idx}.nHIUnitCode`}
                  id={`insuranceUnits.${idx}.nHIUnitCode`}
                  ref={register({
                    required: '必填欄位',
                    pattern: {
                      value: /^\d{9}$/,
                      message: '9碼數字',
                    },
                    validate: v => {
                      const {insuranceUnits} = getValues();
                      return insuranceUnits.some(
                        (insuranceUnit, compareIdx: number) =>
                          insuranceUnit.nHIUnitCode === v && compareIdx !== idx,
                      )
                        ? '健保投保單位代碼不可重複'
                        : true;
                    },
                  })}
                  maxLength={9}
                  error={
                    errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.nHIUnitCode?.message
                  }
                  defaultValue={field.nHIUnitCode}
                  placeholder={'123456789'}
                  onBlur={e => {
                    if (isSubmitted) {
                      trigger(
                        fields
                          .map(
                            (f, fIdx) => `insuranceUnits.${fIdx}.nHIUnitCode`,
                          )
                          .filter((f, fIdx) => fIdx !== idx),
                      );
                    }
                  }}
                />
                <ErrorMessage>
                  {errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.nHIUnitCode?.message}
                </ErrorMessage>
              </FormItemGroup>
              <FormItemGroup className={'min-w-0'}>
                <label htmlFor={`insuranceUnits.${idx}.nHIBusinessGroupCode`}>
                  健保業務組別
                  <RequireMarker />
                </label>
                <Controller
                  render={({onChange, value}) => (
                    <div data-test-id={'nhi-business-group-code'}>
                      <Select
                        id={`insuranceUnits.${idx}.nHIBusinessGroupCode`}
                        placeholder={'臺北業務組別'}
                        getOptionLabel={(o: any) => o.item}
                        getOptionValue={(o: any) => o.value}
                        options={options || []}
                        onChange={(option: any) => {
                          onChange(option.value);
                        }}
                        noOptionsMessage={''}
                        value={options?.find(o => o.value === value)}
                      />
                    </div>
                  )}
                  name={`insuranceUnits.${idx}.nHIBusinessGroupCode`}
                  control={control}
                  rules={{required: '必填欄位'}}
                  error={
                    errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.nHIBusinessGroupCode?.message
                  }
                  defaultValue={field.nHIBusinessGroupCode || ''}
                />
                <ErrorMessage>
                  {errors?.insuranceUnits &&
                    errors.insuranceUnits[idx]?.nHIBusinessGroupCode?.message}
                </ErrorMessage>
              </FormItemGroup>
              <FormItemButton className={'min-w-0'}>
                {fields.length !== 1 && (
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      setDeleteIdx(idx);
                    }}
                    data-test-id={'remove-btn'}
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'trash-alt']}
                      style={{fontSize: 16}}
                    />
                  </IconButton>
                )}
                {idx === fields.length - 1 && (
                  <IconButton
                    onClick={() => append({insuranceApplicantUnitId: uuid()})}
                    data-test-id={'add-btn'}
                  >
                    <FontAwesomeIcon
                      icon={faPlusCircle}
                      style={{fontSize: 16}}
                    />
                  </IconButton>
                )}
              </FormItemButton>
            </FormGroup>
          ))}
        <DeleteDialog
          isOpen={deleteIdx !== null}
          onCancel={() => setDeleteIdx(null)}
          onConfirm={() => {
            if (deleteIdx !== null) {
              remove(deleteIdx);
            }

            setDeleteIdx(null);
          }}
        />
      </Block>
    </div>
  );
};

export default InsuranceApplicantUnit;
